// path: src/Profile.js

import React, { useMemo, useState } from "react";
import "./App.css";
import characters from "./Characters.json";
import { isUsernameAllowed } from "./utils/usernameBlocklist";

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

const getImgSrc = (card) => `imgs/${card.image}`;

const Profile = ({
    username = "",
    onChangeUsername,
    unlockedIds = [],
    selectedProfileImageId = null,
    onSelectProfileImage,
    onCollection,
    onAchievements,
    onBackHome,
    cardsOwned = 0,
    gamesPlayed = 0,
    foilsFound = 0,
}) => {
    const [draftUsername, setDraftUsername] = useState(username);
    const [editingUsername, setEditingUsername] = useState(false);
    const [usernameError, setUsernameError] = useState("");

    const unlockedSet = useMemo(() => {
        return new Set((unlockedIds || []).map(normalizeId));
    }, [unlockedIds]);

    const unlockedCards = useMemo(() => {
        return characters.filter((card) => unlockedSet.has(normalizeId(card.id)));
    }, [unlockedSet]);

    const selectedProfileCard = useMemo(() => {
        const normalizedSelectedId = normalizeId(selectedProfileImageId);

        return (
            unlockedCards.find(
                (card) => normalizeId(card.id) === normalizedSelectedId
            ) ||
            unlockedCards[0] ||
            null
        );
    }, [selectedProfileImageId, unlockedCards]);

    const handleStartEditingUsername = () => {
        setDraftUsername(username);
        setUsernameError("");
        setEditingUsername(true);
    };

    const handleSaveUsername = () => {
        const cleaned = draftUsername.trim();

        if (!cleaned) {
            setUsernameError("Username cannot be empty.");
            return;
        }

        if (!isUsernameAllowed(cleaned)) {
            setUsernameError("That username is not allowed.");
            return;
        }

        if (onChangeUsername) {
            const saved = onChangeUsername(cleaned);

            if (saved === false) {
                setUsernameError("That username is not allowed.");
                return;
            }
        }

        setUsernameError("");
        setEditingUsername(false);
    };

    return (
        <div className="profileScreen">
            <div className="profileHeader">
                <h1 className="impactFont profileTitle">Profile</h1>

                <button className="collectionBackButton" onClick={onBackHome}>
                    Home
                </button>
            </div>

            <div className="profilePanel">
                <div className="profileLeft">
                    <div className="profileUsernameBlock">
                        {!editingUsername ? (
                            <>
                                <div className="profileUsername">{username}</div>
                                <button
                                    type="button"
                                    className="profileSmallButton"
                                    onClick={handleStartEditingUsername}
                                >
                                    Change Username
                                </button>
                            </>
                        ) : (
                            <>
                                <input
                                    className="profileUsernameInput"
                                    value={draftUsername}
                                    onChange={(event) =>
                                        setDraftUsername(event.target.value)
                                    }
                                    maxLength={24}
                                    aria-label="Username"
                                />

                                <div className="profileUsernameActions">
                                    <button
                                        type="button"
                                        className="profileSmallButton"
                                        onClick={handleSaveUsername}
                                    >
                                        Save
                                    </button>

                                    <button
                                        type="button"
                                        className="profileSmallButton profileSmallButton--secondary"
                                        onClick={() => {
                                            setUsernameError("");
                                            setEditingUsername(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>

                                {usernameError && (
                                    <div className="profileUsernameError">
                                        {usernameError}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="profileImageFrame">
                        {selectedProfileCard ? (
                            <img
                                src={getImgSrc(selectedProfileCard)}
                                alt={`${selectedProfileCard.name} profile`}
                                className="profileImage"
                            />
                        ) : (
                            <div className="profileImagePlaceholder">
                                Unlock a card to choose a profile picture.
                            </div>
                        )}
                    </div>

                    <div className="profileSelectedName">
                        {selectedProfileCard
                            ? selectedProfileCard.name
                            : "No profile picture selected"}
                    </div>

                    <div className="profileButtons">
                        <button className="homePrimaryButton" onClick={onCollection}>
                            Collection
                        </button>

                        <button className="homePrimaryButton" onClick={onAchievements}>
                            Achievements
                        </button>
                    </div>
                </div>

                <div className="profileRight">
                    <h2 className="profileSectionTitle">Historical Stats</h2>

                    <div className="profileStats">
                        <div className="profileStat">
                            <div className="profileStatLabel">Cards Owned</div>
                            <div className="profileStatValue">{cardsOwned}</div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Games Played</div>
                            <div className="profileStatValue">{gamesPlayed}</div>
                        </div>

                        <div className="profileStat">
                            <div className="profileStatLabel">Foils Found</div>
                            <div className="profileStatValue">{foilsFound}</div>
                        </div>
                    </div>

                    <h2 className="profileSectionTitle">Choose Profile Picture</h2>

                    {unlockedCards.length > 0 ? (
                        <div className="profileCardPicker">
                            {unlockedCards.map((card) => {
                                const normalizedId = normalizeId(card.id);
                                const selected =
                                    selectedProfileCard &&
                                    normalizeId(selectedProfileCard.id) === normalizedId;

                                return (
                                    <button
                                        type="button"
                                        key={card.id}
                                        className={`profilePickerButton ${
                                            selected ? "profilePickerButton--selected" : ""
                                        }`}
                                        onClick={() => onSelectProfileImage(normalizedId)}
                                        aria-label={`Set ${card.name} as profile picture`}
                                    >
                                        <img
                                            src={getImgSrc(card)}
                                            alt={card.name}
                                            className="profilePickerImage"
                                            loading="lazy"
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="profileEmptyMessage">
                            Your unlocked cards will appear here.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;